/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CFormLabel,
  CRow,
  CSpinner,
  CAlert,
  CBadge
} from '@coreui/react';
import { useTranslation } from '../../../hooks/useTranslation';
import { getSampleDropdown, getSample, getSampleColors, getSampleFactors } from '../../../services/endpoints';
import { formatDateOnly } from '../../../helpers/dateHelper';
import './SampleReport.scss';
import SearchableSelect from '../../../components/SearchableSelect/SearchableSelect';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function SampleReport() {
  const { t } = useTranslation();

  const [loadingDropdown, setLoadingDropdown] = useState(false);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [selectedSampleId, setSelectedSampleId] = useState('');

  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setDropdownItems([]);
    setLoadingDropdown(false);
  }, []);

  const handleSampleSearch = async (searchTerm) => {
    const trimmed = (searchTerm || '').trim();

    if (!trimmed) {
      setDropdownItems([]);
      return;
    }

    try {
      setLoadingDropdown(true);
      setError('');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const res = await getSampleDropdown({
        SessionUserId: sessionUserId,
        ForSampleReport: true,
        SearchTerm: trimmed,
      });
      const data = res?.data || res;
      setDropdownItems(data?.Samples || []);
    } catch (e) {
      console.error('SampleReport dropdown error:', e);
      setError(t('errorLoadingDropdown') || 'Analiz listesi yüklenirken hata oluştu');
      setDropdownItems([]);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const handleGetReport = async () => {
    if (!selectedSampleId) return;
    try {
      setLoadingReport(true);
      setError('');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const [sampleRes, colorRes, factorRes] = await Promise.allSettled([
        getSample({ SessionUserId: sessionUserId, SampleId: selectedSampleId }),
        getSampleColors({ SessionUserId: sessionUserId, PrdSampleGuid: selectedSampleId }),
        getSampleFactors({ SessionUserId: sessionUserId, PrdSampleGuid: selectedSampleId })
      ]);

      if (sampleRes.status === 'rejected') {
        throw sampleRes.reason;
      }

      const data = sampleRes.value?.data || sampleRes.value;
      const sampleList = data?.SampleList || [];
      const sample = sampleList.find(s =>
        s.SampleId === selectedSampleId ||
        s.SampleGuid === selectedSampleId ||
        s.PrdSampleGuid === selectedSampleId
      );

      if (sample) {
        const sampleGuid = sample.PrdSampleGuid || sample.SampleGuid || sample.SampleId || selectedSampleId;
        const sampleColorList = colorRes.status === 'fulfilled'
          ? (colorRes.value?.data || colorRes.value)?.SampleColorList || []
          : [];
        let sampleFactorList = factorRes.status === 'fulfilled'
          ? (factorRes.value?.data || factorRes.value)?.SampleFactorList || []
          : [];

        if (sampleFactorList.length === 0 && sampleGuid) {
          try {
            const fallbackFactorRes = await getSampleFactors({
              SessionUserId: sessionUserId,
              PrdSampleGuid: sampleGuid,
              SampleId: sampleGuid,
            });
            sampleFactorList = (fallbackFactorRes?.data || fallbackFactorRes)?.SampleFactorList || [];
          } catch (factorErr) {
            console.error('SampleReport factor fallback error:', factorErr);
          }
        }
        const normalizedSampleColors = sampleColorList.map((item) => ({
          ...item,
          ColorName: item?.ColorName || '',
          ColorCode: item?.ColorCode || ''
        }));

        const factorGroups = normalizeSampleFactorsByThreadType(sampleFactorList);

        sample.SampleColorList = normalizedSampleColors;
        sample.SampleFactorList = sampleFactorList;
        sample.WarpYarnFromColors = buildThreadColorSummary(normalizedSampleColors, 2);
        sample.WeftYarnFromColors = buildThreadColorSummary(normalizedSampleColors, 1);
        sample.WarpColorText = [...new Set(normalizedSampleColors
          .filter((item) => Number(item?.ThreadType) === 2)
          .map((item) => [item.ColorCode, item.ColorName].filter(Boolean).join(' ').trim())
          .filter(Boolean))].join(' + ');
        sample.WeftColorText = [...new Set(normalizedSampleColors
          .filter((item) => Number(item?.ThreadType) === 1)
          .map((item) => [item.ColorCode, item.ColorName].filter(Boolean).join(' ').trim())
          .filter(Boolean))].join(' + ');
        sample.WarpFactorRanges = factorGroups[2];
        sample.WeftFactorRanges = factorGroups[1];
        sample.WarpFactorText = formatFactorRanges(factorGroups[2]);
        sample.WeftFactorText = formatFactorRanges(factorGroups[1]);

        setReportData(sample);
      } else if (sampleList.length > 0) {
        setReportData(sampleList[0]);
      } else {
        setError(t('noDataFound') || 'Seçilen Analiz için veri bulunamadı');
        setReportData(null);
      }
    } catch (e) {
      console.error('SampleReport fetch error:', e);
      setError(t('errorLoadingReport') || 'Rapor yüklenirken hata oluştu');
      setReportData(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const dropdownOptions = useMemo(() => {
    return (dropdownItems || []).map(item => ({
      value: item.PrdSampleGuid,
      text: `${item.CompanyName || '-'} - ${item.CardNo || '-'}`,
    }));
  }, [dropdownItems]);

  const fmtDate = (d) => {
    if (!d) return '';
    return formatDateOnly(d);
  };

  const fmtNum = (n) => {
    if (n == null || n === '') return '';
    try { return Number(n).toLocaleString('tr-TR'); } catch (e) { return String(n); }
  };

  const normalizeSampleFactorsByThreadType = (items = []) => {
    const grouped = { 1: [], 2: [] };

    if (!Array.isArray(items)) return grouped;

    items.forEach((item) => {
      const threadType = Number(item?.ThreadType ?? item?.threadType);
      const start = Number(item?.FirstColon ?? item?.firstColon ?? item?.Start ?? item?.start);
      const end = Number(item?.SecondColon ?? item?.secondColon ?? item?.End ?? item?.end);
      const factor = Number(item?.Factor ?? item?.factor);

      if (![1, 2].includes(threadType)) return;
      if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(factor)) return;
      if (start <= 0 || end < start || factor <= 0) return;

      grouped[threadType].push({ start, end, factor });
    });

    grouped[1].sort((a, b) => a.start - b.start);
    grouped[2].sort((a, b) => a.start - b.start);

    return grouped;
  };

  const formatFactorRanges = (ranges = []) => {
    if (!Array.isArray(ranges) || ranges.length === 0) return '';

    return ranges
      .map((range) => {
        const start = Number(range.start);
        const end = Number(range.end);
        const factor = Number(range.factor);

        if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(factor)) return '';
        const rangeLabel = start === end ? `${start}` : `${start}-${end}`;
        return `${rangeLabel} x${factor}`;
      })
      .filter(Boolean)
      .join(' + ');
  };

  const buildThreadColorSummary = (sampleColors = [], threadType) => {
    const labels = [];

    (sampleColors || [])
      .filter((item) => Number(item?.ThreadType) === threadType)
      .forEach((item) => {
        const colorLabel = [item?.ColorCode, item?.ColorName].filter(Boolean).join(' ').trim();
        const yarnLabel = [item?.PrdYarnCode, item?.PrdYarnName].filter(Boolean).join(' ').trim();
        const composedLabel = [yarnLabel, colorLabel].filter(Boolean).join(' / ').trim();
        if (composedLabel) labels.push(composedLabel);
      });

    return [...new Set(labels)].join(' + ');
  };

  // Format decimal values (stored as int * 100)
  const fmtDecimal = (value) => {
    if (!value && value !== 0) return '';
    return (parseFloat(value) / 100).toFixed(2);
  };


  const fmtDensityDecimal = (value) => {
    if (!value && value !== 0) return '';
    const parsed = Number(String(value).trim().replace(',', '.'));
    if (!Number.isFinite(parsed)) return String(value);

    const scaled = Math.trunc(Math.abs(parsed));
    const sign = parsed < 0 ? '-' : '';
    const whole = Math.trunc(scaled / 100);
    const decimal = scaled % 100;

    if (decimal === 0) return `${sign}${whole}`;

    return `${sign}${whole},${String(decimal).padStart(2, '0')}`;
  };

  // Get currency symbol
  const getCurrencySymbol = (currencyName, currencyCode) => {
    const code = (currencyCode || '').toLowerCase();
    const name = (currencyName || '').toLowerCase();

    if (code === 'eur' || code === 'euro' || name.includes('euro')) return '€';
    if (code === 'usd' || code === 'dollar' || name.includes('dolar') || name.includes('dollar')) return '$';
    if (code === 'try' || code === 'tl' || name.includes('türk') || name.includes('lira')) return '₺';
    if (code === 'gbp' || name.includes('pound') || name.includes('sterlin')) return '£';
    if (code === 'sar' || name.includes('riyal')) return '﷼';
    return currencyCode || '';
  };

  const buildTemplateHTML = () => {
    if (!reportData) return '';
    return `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Analiz Raporu</title>
    <style>
      ${templateCSS}
    </style>
    </head><body>
      ${buildSampleReportPagesMarkup()}
    </body></html>`;
  };

  const handlePrint = () => {
    const html = buildTemplateHTML();
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.onafterprint = () => w.close();
    }, 400);
  };

  const handleExportExcel = async () => {
    if (!reportData) return;

    const cardNo = reportData.CardNo || '';
    const companyName = reportData.CompanyName || '';
    const fabricWidth = reportData.FabricWidth || '';
    const fabricWeight = (reportData.FabricWeight === null || reportData.FabricWeight === undefined || reportData.FabricWeight === '' ? '' : String(reportData.FabricWeight));
    const weftDensity = fmtDensityDecimal(reportData.WeftDensity) || '';
    const warpDensity = fmtDensityDecimal(reportData.WarpDensity) || '';
    const compositionName = reportData.CompositionName || '';
    const warpYarn = reportData.WarpYarnFromColors || reportData.WarpYarn || reportData.WarpYarnName || '';
    const weftYarn = reportData.WeftYarnFromColors || reportData.WeftYarn || reportData.WeftYarnName || '';
    const reedNo = reportData.ReedNo || '';
    const dgts = reportData.Dgts || '';
    const reedWidth = reportData.ReedWidth || '';
    const totalThread = fmtNum(reportData.TotalThread) || '';
    const weaveTypeName = reportData.WeaveTypeName || '';
    const grayWeft = fmtDensityDecimal(reportData.GrayWeft) || '';
    const grayWarp = fmtDensityDecimal(reportData.GrayWarp) || '';
    const price = fmtDecimal(reportData.Price) || '';
    const currencySymbol = getCurrencySymbol(reportData.CurrencyName, reportData.CurrencyCode);
    const note = reportData.Note || '';
    const tarakNoStr = reedNo && dgts ? `${reedNo}*${dgts}` : (reedNo || dgts || '');
    const mamulSikliklariStr = warpDensity && weftDensity ? `${warpDensity}*${weftDensity}` : (warpDensity || weftDensity || '');
    const hamSikliklariStr = grayWarp && grayWeft ? `${grayWarp}*${grayWeft}` : (grayWarp || grayWeft || '');
    const priceStr = price ? `${price} ${currencySymbol}` : '';
    const tarihStr = fmtDate(reportData.Datetime) || new Date().toLocaleDateString('tr-TR');

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Kumaş Analiz Formu');

    // Sütun genişlikleri (3 sütun)
    ws.columns = [
      { width: 24 }, // A - Label
      { width: 50 }, // B - Value (wider)
      { width: 30 }, // C - KART NO / NOT
    ];

    const borderAll = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    const boldFont = { bold: true, size: 11, name: 'Arial' };
    const normalFont = { size: 11, name: 'Arial' };
    const titleFont = { bold: true, size: 13, name: 'Arial' };
    const labelFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // --- Logo fetch ---
    let logoImageId = null;
    try {
      const logoResponse = await fetch(`${window.location.origin}/logo.jpg`);
      const logoBlob = await logoResponse.blob();
      const logoBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(logoBlob);
      });
      logoImageId = workbook.addImage({ base64: logoBase64, extension: 'jpeg' });
    } catch (e) {
      console.warn('Logo yüklenemedi:', e);
    }

    // R1: Header - Logo | Title | Tarih
    const row1 = ws.addRow(['', 'FABCO TEKSTİL KUMAŞ ANALİZ FORMU', '']);
    row1.height = 30;
    row1.getCell(2).font = titleFont;
    row1.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
    row1.getCell(3).value = { richText: [
      { text: 'Tarih: ', font: { bold: true, size: 11, name: 'Arial' } },
      { text: tarihStr, font: { bold: false, size: 11, name: 'Arial' } }
    ] };
    row1.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    row1.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { bottom: { style: 'medium' }, top: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });
    if (logoImageId !== null) {
      ws.addImage(logoImageId, { tl: { col: 0, row: 0 }, ext: { width: 90, height: 24 }, editAs: 'oneCell' });
    }

    // R2: FİRMA ADI / KART NO
    const row2 = ws.addRow(['FİRMA ADI:', companyName, '']);
    row2.getCell(3).value = { richText: [
      { text: 'KART NO: ', font: { bold: true, size: 11, name: 'Arial' } },
      { text: cardNo, font: { bold: false, size: 11, name: 'Arial' } }
    ] };
    row2.eachCell({ includeEmpty: true }, (cell, colNum) => {
      cell.border = borderAll;
      cell.font = (colNum === 1) ? boldFont : (colNum === 3 ? undefined : normalFont);
      cell.alignment = { vertical: 'middle', horizontal: colNum === 3 ? 'center' : undefined };
      if (colNum === 1) cell.fill = labelFill;
    });

    // Data rows (R3-R13) - Left side label+value, Right side NOT merged
    const dataRows = [
      ['MAMÜL EN:', `${fabricWidth}${fabricWidth ? ' CM' : ''}`],
      ['MAMÜL GR/M2:', `${fabricWeight}${fabricWeight ? ' GR/M2' : ''}`],
      ['MAMÜL SIKLIKLARI:', mamulSikliklariStr],
      ['KARIŞIM:', compositionName],
      ['ÇÖZGÜ İPLİK NUMARASI:', warpYarn],
      ['ATKI İPLİK NUMARASI:', weftYarn],
      ['TARAK NO:', tarakNoStr],
      ['TARAK ENİ:', `${reedWidth}${reedWidth ? ' CM' : ''}`],
      ['TOPLAM TEL SAYISI:', `${totalThread}${totalThread ? ' TEL' : ''}`],
      ['ÖRGÜ:', weaveTypeName],
      ['HAM SIKLIKLARI:', hamSikliklariStr],
    ];

    // R3-R13: Data rows + NOT merged area (C3:C14)
    for (let i = 0; i < dataRows.length; i++) {
      const rowNum = 3 + i;
      const r = ws.getRow(rowNum);
      r.getCell(1).value = dataRows[i][0];
      r.getCell(2).value = dataRows[i][1];
      r.getCell(1).font = boldFont;
      r.getCell(1).fill = labelFill;
      r.getCell(1).border = borderAll;
      r.getCell(2).font = normalFont;
      r.getCell(2).border = borderAll;
      r.getCell(2).alignment = { vertical: 'middle', wrapText: true };
      r.getCell(1).alignment = { vertical: 'middle' };
      if (i === 0) {
        r.getCell(3).value = { richText: [
          { text: 'NOT: ', font: { bold: true, size: 11, name: 'Arial' } },
          { text: note, font: { bold: false, size: 11, name: 'Arial' } }
        ] };
      }
      r.getCell(3).border = borderAll;
      r.commit();
    }
    ws.mergeCells('C3:C14');
    // Style the NOT merged cell
    const notCell = ws.getRow(3).getCell(3);
    notCell.font = normalFont;
    notCell.alignment = { vertical: 'top', wrapText: true };
    notCell.border = borderAll;

    // R14: FİYAT
    const r14 = ws.getRow(14);
    r14.getCell(1).value = 'FİYAT:';
    r14.getCell(2).value = priceStr;
    r14.getCell(1).font = boldFont;
    r14.getCell(1).fill = labelFill;
    r14.getCell(2).font = normalFont;
    [1, 2, 3].forEach(c => { r14.getCell(c).border = borderAll; });

    // --- Photo ---
    const photo = reportData.Photo || '';
    if (photo) {
      let photoBase64 = '';
      let photoExt = 'jpeg';
      if (photo.startsWith('data:image')) {
        // data:image/jpeg;base64,xxxx veya data:image/png;base64,xxxx
        const match = photo.match(/^data:image\/(\w+);base64,(.+)$/);
        if (match) {
          photoExt = match[1] === 'png' ? 'png' : 'jpeg';
          photoBase64 = match[2];
        }
      } else {
        // Pure base64 string
        photoBase64 = photo;
      }

      if (photoBase64) {
        const photoImageId = workbook.addImage({ base64: photoBase64, extension: photoExt });
        // Fotoğrafı tablonun altına yerleştir (row 15, A sütunundan başla)
        // Satır 15'e boşluk bırak
        ws.getRow(15).height = 20;
        ws.addImage(photoImageId, {
          tl: { col: 0, row: 15 },
          ext: { width: 400, height: 300 },
        });
      }
    }

    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `kumas_analiz_${cardNo || 'rapor'}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const formatReportNumber = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(2).replace(/\.?0+$/, '');
  };

  const processSampleColors = (sampleColors = []) => {
    const warpColors = [];
    const weftColors = [];
    const maxPosition = Math.max(...(sampleColors || []).map((item) => Number(item?.Position) || 0), 0);

    for (let i = 1; i <= maxPosition; i += 1) {
      warpColors[i] = { position: i, colors: [] };
      weftColors[i] = { position: i, colors: [] };
    }

    (sampleColors || []).forEach((sc) => {
      const position = Number(sc?.Position) || 1;
      const threadType = Number(sc?.ThreadType) || 0;
      const colorData = {
        yarnName: sc?.PrdYarnName || '',
        yarnCode: sc?.PrdYarnCode || '',
        colorName: sc?.ColorName || '',
        colorCode: sc?.ColorCode || '',
        sampleRowSorting: Number(sc?.SampleRowSorting) || null,
        threadCount: Number(sc?.ThreadCount) || 0,
        weight: threadType === 1 ? sc?.WeftWeight : sc?.WaftWeight,
      };

      if (threadType === 1) {
        if (!weftColors[position]) weftColors[position] = { position, colors: [] };
        weftColors[position].colors.push(colorData);
      } else if (threadType === 2) {
        if (!warpColors[position]) warpColors[position] = { position, colors: [] };
        warpColors[position].colors.push(colorData);
      }
    });

    return { warpColors, weftColors, maxPosition };
  };

  const buildColorRows = (colors, reportMaxPosition = 0, factorRanges = []) => {
    const colorGroups = {};

    (colors || []).forEach((posData) => {
      if (posData && posData.colors) {
        posData.colors.forEach((color) => {
          const key = `${color.yarnName}_${color.yarnCode}_${color.colorName}_${color.colorCode}`;
          if (!colorGroups[key]) {
            colorGroups[key] = {
              yarnName: color.yarnName,
              yarnCode: color.yarnCode,
              colorName: color.colorName,
              colorCode: color.colorCode,
              sampleRowSorting: color.sampleRowSorting || Number.MAX_SAFE_INTEGER,
              weight: color.weight || '',
              positions: {}
            };
          }
          colorGroups[key].positions[posData.position] = color.threadCount;
          if (color.sampleRowSorting && color.sampleRowSorting < colorGroups[key].sampleRowSorting) {
            colorGroups[key].sampleRowSorting = color.sampleRowSorting;
          }
        });
      }
    });

    const colorArray = Object.values(colorGroups);

    colorArray.forEach((color) => {
      let total = 0;
      for (let i = 1; i <= Math.max(reportMaxPosition, 25); i += 1) {
        const threadCount = Number(color.positions[i]) || 0;
        const factorRange = factorRanges.find((range) => i >= range.start && i <= range.end);
        const factor = factorRange ? Number(factorRange.factor) : 1;
        total += threadCount * (Number.isFinite(factor) && factor > 0 ? factor : 1);
      }
      color.total = Number(total.toFixed(2));
    });

    const grandTotal = Number(colorArray.reduce((sum, color) => sum + color.total, 0).toFixed(2));

    colorArray.forEach((color) => {
      color.percentage = grandTotal > 0 ? ((color.total / grandTotal) * 100).toFixed(1) : '0.0';
    });

    colorArray.sort((a, b) => {
      const rowSortingDiff = (a.sampleRowSorting || Number.MAX_SAFE_INTEGER) -
        (b.sampleRowSorting || Number.MAX_SAFE_INTEGER);
      if (rowSortingDiff !== 0) return rowSortingDiff;

      const aName = (a.colorName || '') + '-' + (a.yarnName || '');
      const bName = (b.colorName || '') + '-' + (b.yarnName || '');
      return aName.localeCompare(bName, 'tr');
    });

    return {
      rows: colorArray,
      grandTotal,
    };
  };

  const renderColorReport = ({
    displayRows = [],
    grandTotal = 0,
    title,
    yarnLabel,
    startPosition = 1,
    showTotalRow = true,
    factorRanges = [],
  }) => {
    const DISPLAY_COLS = 25;
    const pagePositions = Array.from({ length: DISPLAY_COLS }, (_, idx) => startPosition + idx);

    const getFactorBackground = (position) => {
      const factorRangeIndex = factorRanges.findIndex(
        (range) => position >= range.start && position <= range.end
      );
      if (factorRangeIndex === -1) return '';
      return factorRangeIndex % 2 === 0 ? '#fff59d' : '#c8e6c9';
    };

    const getFactorCellStyle = (
      position,
      hideColumnBorders = false,
      includeBackground = true,
      showBoundaryLines = false,
    ) => {
      const background = includeBackground ? getFactorBackground(position) : '';
      const styles = [];
      if (background) styles.push(`background:${background}`);

      const factorRange = factorRanges.find(
        (range) => position >= range.start && position <= range.end
      );

      if (hideColumnBorders) {
        styles.push('border-left:none !important');
        styles.push('border-right:none !important');

        if (factorRange && showBoundaryLines) {
          const shadowParts = [];
          if (position === factorRange.start) shadowParts.push('inset 2px 0 0 #000');
          if (position === factorRange.end) shadowParts.push('inset -2px 0 0 #000');
          if (shadowParts.length > 0) styles.push(`box-shadow:${shadowParts.join(',')}`);
        }
      } else if (factorRange && showBoundaryLines) {
        if (position === factorRange.start) styles.push('border-left:2px solid #000 !important');
        if (position === factorRange.end) styles.push('border-right:2px solid #000 !important');
      }

      return styles.length > 0 ? ` style="${styles.join(';')};"` : '';
    };

    const safe = (value) => {
      const normalized = value == null ? '' : String(value);
      return normalized ? escapeHtml(normalized) : '';
    };

    // Build consistent colgroup for all rows
    const colGroupHTML = `<colgroup>
      <col style="width:65px;" />
      <col style="width:150px;" />
      <col style="width:75px;" />
      <col style="width:55px;" />
      ${Array.from({ length: DISPLAY_COLS }, () => '<col style="width:22px;" />').join('')}
      <col style="width:50px;" />
      <col style="width:50px;" />
      <col style="width:65px;" />
    </colgroup>`;

    return `
      <div class="color-report-section">
        <table class="color-report-table">
          ${colGroupHTML}
          <thead>
            <tr>
              <th colspan="${DISPLAY_COLS + 7}" class="color-report-title">${escapeHtml(title)}</th>
            </tr>
            <tr class="yarn-label-row">
              <td colspan="${DISPLAY_COLS + 4}" style="border:1px solid #000;"></td>
              <td colspan="3" style="border:1px solid #000;text-align:center;font-weight:600;white-space:nowrap;">${escapeHtml(yarnLabel)}</td>
            </tr>
            <tr>
              <th class="yarn-code">İPLİK NUMARASI</th>
              <th class="yarn-name">İPLİK ADI</th>
              <th class="color-name">RENK ADI</th>
              <th class="color-code">RENK KODU</th>
              ${pagePositions.map((position) => `<th class="position-col"${getFactorCellStyle(position)}>${position}</th>`).join('')}
              <th class="total-col">Toplam</th>
              <th class="percentage-col">%</th>
              <th class="weight-col">İPLİK GRAMAJI</th>
            </tr>
          </thead>
          <tbody>
            ${displayRows.map((color) => `
              <tr>
                <td class="yarn-code">${safe(color.yarnCode)}</td>
                <td class="yarn-name">${safe(color.yarnName)}</td>
                <td class="color-name">${safe(color.colorName)}</td>
                <td class="color-code">${safe(color.colorCode)}</td>
                ${pagePositions.map((position) => `<td class="position-col"${getFactorCellStyle(position)}>${safe(color.positions[position])}</td>`).join('')}
                <td class="total-col">${safe(formatReportNumber(color.total))}</td>
                <td class="percentage-col">${color.yarnName ? `${color.percentage}%` : ''}</td>
                <td class="weight-col">${color.weight ? (Number(color.weight) / 10000).toFixed(2) : ''}</td>
              </tr>
            `).join('')}
            ${showTotalRow ? `<tr class="toplam-row">
              <td class="total-label" colspan="4" style="text-align:right;font-weight:700;white-space:nowrap;">Toplam</td>
              ${pagePositions.map((position) => {
                const factorStyle = getFactorCellStyle(position, false, true, false);
                return `<td class="position-col"${factorStyle}></td>`;
              }).join('')}
              <td class="total-col" style="white-space:nowrap;"><strong>${formatReportNumber(grandTotal)}</strong></td>
              <td class="percentage-col" style="white-space:nowrap;"><strong>100.0%</strong></td>
              <td class="weight-col"></td>
            </tr>` : ''}
            <tr class="factor-row">
              <td class="color-name" colspan="4" style="text-align:right;font-weight:700;white-space:nowrap;">Çarpan</td>
              ${(() => {
                const cells = [];
                let positionIndex = 0;

                while (positionIndex < pagePositions.length) {
                  const position = pagePositions[positionIndex];
                  const factorRangeIndex = factorRanges.findIndex(
                    (range) => position >= range.start && position <= range.end
                  );

                  if (factorRangeIndex === -1) {
                    cells.push('<td class="position-col"></td>');
                    positionIndex += 1;
                    continue;
                  }

                  const factorRange = factorRanges[factorRangeIndex];
                  const visibleStart = Math.max(factorRange.start, startPosition);
                  const visibleEnd = Math.min(factorRange.end, startPosition + DISPLAY_COLS - 1);
                  const visibleSpan = Math.max(1, visibleEnd - visibleStart + 1);
                  const background = factorRangeIndex % 2 === 0 ? '#fff59d' : '#c8e6c9';
                  const label = `X${formatReportNumber(factorRange.factor)}`;

                  if (position < visibleStart) {
                    const bgStyle = getFactorCellStyle(position, false, true, false);
                    cells.push(`<td class="position-col"${bgStyle}></td>`);
                    positionIndex += 1;
                    continue;
                  }

                  cells.push(`<td class="position-col factor-range-merged" colspan="${visibleSpan}" style="background:${background};text-align:center;font-weight:700;">${escapeHtml(label)}</td>`);
                  positionIndex += visibleSpan;
                }

                return cells.join('');
              })()}
              <td class="total-col"></td>
              <td class="percentage-col"></td>
              <td class="weight-col"></td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  };

  const renderSampleColorReportMarkup = ({ reportData }) => {
    if (!reportData) return '';

    const DISPLAY_COLS = 25;
    const TOTAL_TABLE_COLS = DISPLAY_COLS + 7;
    const safeLogoUrl = '';
    const cardNo = escapeHtml(reportData.CardNo || '');
    const companyName = escapeHtml(reportData.CompanyName || '');
    const note = escapeHtml(reportData.Note || '');
    const compositionName = escapeHtml(reportData.CompositionName || '');
    const reedNo = escapeHtml(reportData.ReedNo || '');
    const dgts = escapeHtml(reportData.Dgts || '');
    const reedWidth = escapeHtml(reportData.ReedWidth || '');
    const warpDensity = escapeHtml(fmtDensityDecimal(reportData.WarpDensity) || '');
    const weftDensity = escapeHtml(fmtDensityDecimal(reportData.WeftDensity) || '');
    const totalThread = escapeHtml(fmtNum(reportData.TotalThread) || '');
    const weaveTypeName = escapeHtml(reportData.WeaveTypeName || '');
    const tarakNoStr = reedNo && dgts ? `${reedNo}*${dgts}` : (reedNo || dgts || '');
    const sampleColors = reportData.SampleColorList || [];
    const warpFactorRanges = reportData.WarpFactorRanges || [];
    const weftFactorRanges = reportData.WeftFactorRanges || [];
    const { warpColors, weftColors, maxPosition } = processSampleColors(sampleColors);
    const warpData = buildColorRows(warpColors, maxPosition, warpFactorRanges);
    const weftData = buildColorRows(weftColors, maxPosition, weftFactorRanges);
    const renderHeaderColGroup = () => '';
    const descriptionColspan = DISPLAY_COLS;
    const brandColspan = TOTAL_TABLE_COLS - 4 - descriptionColspan;

    return `
      <div class="sample-report-page report-page-break">
      <div class="report-wrap sample-report-rotated-content">
        <div class="page-meta-bar">
          <div class="page-meta-left">
            <div class="tip-code-box">
              <span class="tip-label">KART NO</span>
              <span class="tip-value">${cardNo}</span>
            </div>
          </div>
          <div class="page-number-box">Sayfa 2</div>
        </div>
        <table class="header-table">
          ${renderHeaderColGroup()}
          <tr>
            <td class="label-cell"><strong>KART NO</strong></td>
            <td class="value-cell">${cardNo}</td>
            <td class="label-cell"><strong>TARAK NO</strong></td>
            <td class="value-cell">${tarakNoStr}</td>
            <td class="description-area" colspan="${descriptionColspan}" rowspan="4" align="left" valign="top">
              <div class="description-title"><strong>AÇIKLAMA:</strong></div>
              <div class="description-text">${note || '&nbsp;'}</div>
            </td>
            <td class="brand-area" colspan="${brandColspan}" rowspan="4">
              ${safeLogoUrl ? `<img src="${safeLogoUrl}" alt="FABCO" /><br/>` : ''}
              <div class="company-name">${companyName || 'Tekstil San. Ve Tic.A.Ş'}</div>
              <div class="report-main-title">ÇÖZGÜ VE ATKI RENK RAPORLARI</div>
            </td>
          </tr>
          <tr>
            <td class="label-cell"><strong>KOMPOZİSYON</strong></td>
            <td class="value-cell">${compositionName}</td>
            <td class="label-cell"><strong>TARAK ENİ</strong></td>
            <td class="value-cell">${reedWidth}</td>
          </tr>
          <tr>
            <td class="label-cell"><strong>ÇÖZGÜ SIKLIĞI</strong></td>
            <td class="value-cell">${warpDensity}</td>
            <td class="label-cell"><strong>TOPLAM TEL ADEDİ</strong></td>
            <td class="value-cell">${totalThread}</td>
          </tr>
          <tr>
            <td class="label-cell"><strong>ATKI SIKLIĞI</strong></td>
            <td class="value-cell">${weftDensity}</td>
            <td class="label-cell"><strong>ÖRGÜ</strong></td>
            <td class="value-cell">${weaveTypeName}</td>
          </tr>
        </table>
        <div class="sample-report-brand-bar">
          <div class="sample-report-brand-logo">
            ${safeLogoUrl ? `<img src="${safeLogoUrl}" alt="FABCO" />` : ''}
          </div>
          <div class="sample-report-brand-copy">
            <div class="sample-report-main-title">ÇÖZGÜ VE ATKI RENK RAPORU</div>
            ${companyName ? `<div class="sample-report-company">${companyName}</div>` : ''}
          </div>
        </div>
        ${renderColorReport({
          displayRows: warpData.rows,
          grandTotal: warpData.grandTotal,
          title: 'ÇÖZGÜ RENK RAPORU',
          yarnLabel: 'Çözgü İpliği',
          startPosition: 1,
          showTotalRow: true,
          factorRanges: warpFactorRanges,
        })}
        ${renderColorReport({
          displayRows: weftData.rows,
          grandTotal: weftData.grandTotal,
          title: 'ATKI RENK RAPORU',
          yarnLabel: 'Atkı İpliği',
          startPosition: 1,
          showTotalRow: true,
          factorRanges: weftFactorRanges,
        })}
      </div>
      </div>
    `;
  };

  const buildSampleReportPagesMarkup = () => {
    if (!reportData) return '';

    const logoUrl = `${window.location.origin}/logo.jpg`;
    const tarihStr = fmtDate(reportData.Datetime);

    return `
      ${renderTemplateMarkup({ logoUrl, tarihStr, reportData, fmtDecimal, fmtDensityDecimal, getCurrencySymbol, fmtNum, t, includePhoto: true })}
      ${renderSampleColorReportMarkup({ logoUrl, reportData })}
    `;
  };

  const resetForm = () => {
    setSelectedSampleId('');
    setReportData(null);
    setError('');
  };

  return (
    <div className="sample-report">
      <CContainer fluid className="px-4">
        <div className="page-header mb-4">
          <h2 className="page-title">
            <i className="fas fa-file-alt"></i>
            {t('sampleReport') || 'Analiz Raporu'}
          </h2>
          <p className="page-subtitle text-muted">
            {t('sampleReportDescription') || 'Analiz seçerek kumaş analiz formunu görüntüleyebilir, yazdırabilir veya Excel formatında indirebilirsiniz.'}
          </p>
        </div>

        {error && (
          <CAlert color="danger" dismissible onClose={() => setError('')} className="mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </CAlert>
        )}

        <CRow className="mb-4 justify-content-center">
          <CCol xs={12} xl={10} xxl={8}>
            <CCard className="shadow-sm border-0 selection-card">
              <CCardHeader className="gradient-header text-white">
                <div className="d-flex align-items-center">
                  <i className="fas fa-search me-2"></i>
                  <h5 className="mb-0">{t('sampleSelection') || 'Analiz Seçimi'}</h5>
                </div>
              </CCardHeader>
              <CCardBody className="p-4">
                <CRow className="g-3">
                  <CCol xs={12} lg={6}>
                    <CFormLabel htmlFor="sampleSelect" className="form-label-required">
                      {t('selectSample') || 'Analiz Seçin'}
                    </CFormLabel>
                    <SearchableSelect
                      id="sampleSelect"
                      options={dropdownOptions}
                      value={selectedSampleId}
                      onChange={(v) => setSelectedSampleId(v)}
                      onSearchTermChange={handleSampleSearch}
                      placeholder={loadingDropdown ? (t('loading') || 'Yükleniyor...') : (t('searchSamplePlaceholder') || 'Kart no ile ara...')}
                      displayKey="text"
                      valueKey="value"
                      disabled={loadingDropdown}
                    />
                    {dropdownOptions.length > 0 && (
                      <small className="text-muted mt-1 d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        {dropdownOptions.length} Analiz mevcut
                      </small>
                    )}
                  </CCol>
                  <CCol xs={12} lg={6}>
                    <CFormLabel className="d-none d-lg-block" style={{ visibility: 'hidden' }}>
                      &nbsp;
                    </CFormLabel>
                    <div className="action-buttons-group">
                      <CButton
                        className="btn-report-generate"
                        onClick={handleGetReport}
                        disabled={!selectedSampleId || loadingReport || loadingDropdown}
                        title={t('generateReport') || 'Raporu Getir'}
                      >
                        {loadingReport ? (
                          <>
                            <CSpinner size="sm" className="me-2" />
                            {t('loading') || 'Yükleniyor...'}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-alt me-2"></i>
                            {t('generateReport') || 'Raporu Getir'}
                          </>
                        )}
                      </CButton>
                      {(selectedSampleId || reportData) && (
                        <CButton
                          className="btn-clear"
                          onClick={resetForm}
                          disabled={loadingReport || loadingDropdown}
                          title={t('clear') || 'Temizle'}
                        >
                          <span className="clear-icon">×</span>
                          <span className="btn-text">{t('clear') || 'Temizle'}</span>
                        </CButton>
                      )}
                    </div>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {reportData && (
          <>
            <CRow className="mb-4 justify-content-center">
              <CCol xs={12} xl={10} xxl={8}>
                <CCard className="shadow-sm border-0 details-card">
                  <CCardHeader className="details-header">
                    <div className="details-header-content">
                      <div className="details-info">
                        <h5 className="mb-2 details-title">
                          <i className="fas fa-file-alt me-2"></i>
                          {t('reportDetails') || 'Rapor Detayları'}
                        </h5>
                        <div className="details-badges">
                          <CBadge className="detail-badge code-badge">
                            <i className="fas fa-barcode me-1"></i>
                            {reportData.CardNo}
                          </CBadge>
                          {reportData.CompanyName && (
                            <CBadge className="detail-badge type-badge">
                              <i className="fas fa-building me-1"></i>
                              {reportData.CompanyName}
                            </CBadge>
                          )}
                          {reportData.Datetime && (
                            <CBadge className="detail-badge date-badge">
                              <i className="fas fa-calendar me-1"></i>
                              {fmtDate(reportData.Datetime)}
                            </CBadge>
                          )}
                        </div>
                      </div>
                      <div className="action-buttons-export">
                        <CButton
                          className="btn-action-print"
                          size="sm"
                          onClick={handlePrint}
                          disabled={!reportData}
                        >
                          <i className="fas fa-print me-1"></i>
                          <span className="btn-text">{t('print') || 'Yazdır'}</span>
                        </CButton>
                        <CButton
                          className="btn-action-excel"
                          size="sm"
                          onClick={handleExportExcel}
                          disabled={!reportData}
                        >
                          <i className="fas fa-file-excel me-1"></i>
                          <span className="btn-text">Excel</span>
                        </CButton>
                      </div>
                    </div>
                  </CCardHeader>
                </CCard>
              </CCol>
            </CRow>

            <CRow>
              <CCol xs={12}>
                <CCard className="report-preview-card shadow">
                  <CCardBody className="p-0">
                    <div className="report-a4-preview">
                      <div dangerouslySetInnerHTML={{
                        __html: buildSampleReportPagesMarkup()
                      }} />
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </>
        )}
      </CContainer>
    </div>
  );
}

export default SampleReport;

// CSS for the print template
const templateCSS = `
  @page { size: A4 portrait; margin: 10mm; }
  @page sample-color-report-page { size: A4 landscape; margin: 10mm; }
  @media print {
    @page { size: A4 portrait; margin: 10mm; }
    @page sample-color-report-page { size: A4 landscape; margin: 10mm; }
    html, body {
      width: auto;
      height: auto;
    }
    .report-page-break {
      break-before: page;
      page-break-before: always;
    }
  }
  body { 
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
    color: #333; 
    line-height: 1.3;
    margin: 0;
    padding: 0;
    font-size: 15px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .report-wrap { 
    width: 100%; 
    max-width: 100%;
    border: 1px solid #000; 
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
    box-sizing: border-box;
  }
  .sample-main-page {
    min-height: 277mm;
  }
  .sample-main-photo {
    margin-top: 10mm;
    text-align: left;
    padding-left: 4px;
  }
  .sample-main-photo img {
    height: 220px;
    width: 520px;
    object-fit: fill;
  }
  .header-grid { 
    display: grid; 
    grid-template-columns: 25% 50% 25%; 
    border-bottom: 1px solid #000; 
    background: #ffffff;
  }
  .logo-cell { 
    border-right: 1px solid #000; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    padding: 12px 8px; 
    background: #ffffff;
  }
  .title-cell { 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    font-weight: 700; 
    font-size: 20px; 
    color: #000;
    text-align: center;
    padding: 12px;
    letter-spacing: 1px;
  }
  .meta-cell { 
    border-left: 1px solid #000; 
    padding: 8px; 
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .meta-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 13px; 
  }
  .meta-table td { 
    border: 1px solid #000; 
    padding: 6px 8px; 
    background: #ffffff;
    vertical-align: middle;
  }
  .meta-table strong {
    color: #000;
    font-weight: 600;
  }
  .info-table {
    width: 100%; 
    border-collapse: collapse; 
    table-layout: fixed;
    font-size: 15px;
    margin: 0;
  }
  .info-table td {
    border: 1px solid #000;
    padding: 7px 11px;
    background: #ffffff;
    vertical-align: middle;
  }
  .info-table tr:first-child td {
    border-top: none;
  }
  .info-table .label-cell {
    background: #f8f9fa;
    font-weight: 600;
    width: 20%;
    color: #000;
  }
  .info-table .value-cell {
    background: #ffffff;
    color: #000;
    width: 50%;
  }
  .info-table .note-cell {
    background: #ffffff;
    color: #000;
    width: 30%;
    word-break: break-all;
    overflow-wrap: break-word;
  }
  .photo-section {
    text-align: center;
    padding: 20px;
    border-top: 1px solid #000;
  }
  .photo-section img {
    max-width: 100%;
    max-height: 250px;
    object-fit: contain;
    border: 1px solid #ddd;
  }
  .report-page-break {
    margin-top: 0;
    break-before: page;
    page-break-before: always;
  }
  .sample-report-page {
    page: sample-color-report-page;
    width: 100%;
    min-height: 190mm;
    max-width: 100%;
    margin: 0;
    border: none;
    overflow: visible;
    position: relative;
    box-sizing: border-box;
  }
  .sample-report-page .header-table,
  .sample-report-page .sample-report-brand-bar {
    display: none !important;
  }
  .sample-report-rotated-content {
    width: 100%;
    max-width: 100%;
    min-height: auto;
    margin: 0;
    overflow: visible;
  }
  .page-meta-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .page-meta-left {
    flex: 1;
    display: flex;
    align-items: center;
  }
  .tip-code-box {
    display: inline-flex;
    align-items: center;
    border: 2px solid #000;
    background: #fff;
    font-size: 13px;
    line-height: 1;
    min-height: 34px;
  }
  .tip-code-box .tip-label {
    font-weight: 700;
    background: #f0f0f0;
    border-right: 2px solid #000;
    padding: 8px 10px;
    white-space: nowrap;
  }
  .tip-code-box .tip-value {
    font-weight: 600;
    padding: 8px 12px;
    min-width: 120px;
  }
  .page-number-box {
    border: 2px solid #000;
    background: #fff;
    font-size: 13px;
    font-weight: 700;
    padding: 7px 12px;
    line-height: 1;
    white-space: nowrap;
  }
  .sample-report-brand-bar {
    display: none;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 2px solid #000;
    background: #fff;
    padding: 8px 10px;
    margin-bottom: 8px;
  }
  .sample-report-brand-logo {
    flex: 0 0 90px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sample-report-brand-logo img {
    max-height: 36px;
    max-width: 90px;
    object-fit: contain;
  }
  .sample-report-brand-copy {
    flex: 1;
    text-align: center;
  }
  .sample-report-main-title {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    letter-spacing: 0.5px;
    margin: 0;
  }
  .sample-report-company {
    font-size: 12px;
    font-weight: 600;
    margin-top: 4px;
    color: #000;
  }
  .header-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    margin-bottom: 8px;
    table-layout: fixed;
    border: 2px solid #000;
  }
  .header-table td {
    border: 2px solid #000;
    padding: 6px 8px;
    vertical-align: middle;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.15;
  }
  .header-table .label-cell {
    font-weight: 700;
    background: #f0f0f0;
    white-space: normal;
    color: #000;
    width: 145px;
  }
  .header-table .value-cell {
    background: #ffffff;
    color: #000;
    width: 120px;
  }
  .header-table .description-area {
    text-align: left !important;
    vertical-align: top !important;
    background: #ffffff;
    padding: 4px 8px 0 8px;
  }
  .header-table .description-area .description-title {
    font-size: 12px;
    font-weight: 800;
    color: #000;
    margin: 0;
    line-height: 1.1;
  }
  .header-table .description-area .description-text {
    font-size: 12px;
    color: #000;
    line-height: 1.2;
    margin-top: 2px;
  }
  .header-table .brand-area {
    text-align: center;
    background: #ffffff;
    color: #000;
    font-weight: 700;
  }
  .header-table .brand-area img {
    max-height: 28px;
    max-width: 110px;
    object-fit: contain;
    margin-bottom: 2px;
  }
  .header-table .brand-area .company-name {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 3px;
  }
  .header-table .brand-area .report-main-title {
    font-size: 13px;
    font-weight: 800;
  }
  .color-report-section {
    margin: 8px 0;
  }
  .color-report-title {
    font-size: 15px;
    font-weight: 700;
    color: #000;
    padding: 6px 10px;
    border: 2px solid #000;
    border-bottom: none;
    background: #f0f0f0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    table-layout: fixed;
    border: 2px solid #000;
  }
  .color-report-table th,
  .color-report-table td {
    border: 1px solid #000;
    padding: 3px 2px;
    text-align: center;
    background: #ffffff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    height: 20px;
  }
  .color-report-table th {
    background: #f0f0f0;
    font-weight: 600;
    color: #000;
  }
  .color-report-table .yarn-code {
    text-align: center;
    width: 65px;
    max-width: 65px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .yarn-name {
    text-align: center;
    width: 150px;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .color-name {
    text-align: center;
    width: 75px;
    max-width: 75px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .color-code {
    text-align: center;
    width: 55px;
    max-width: 55px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .total-col {
    background: #e9ecef;
    font-weight: 600;
    width: 50px;
    max-width: 50px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .percentage-col {
    background: #e9ecef;
    font-weight: 600;
    width: 50px;
    max-width: 50px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .weight-col {
    background: #e9ecef;
    width: 65px;
    max-width: 65px;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .color-report-table .position-col {
    width: 22px;
    min-width: 22px;
    max-width: 22px;
    white-space: nowrap;
    overflow: hidden;
  }
  .color-report-table .total-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .yarn-label-row td {
    border: 1px solid #000;
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    padding: 2px 3px;
    white-space: nowrap;
    overflow: hidden;
  }
  .color-report-table tbody tr.toplam-row td {
    border: 1px solid #000;
    border-bottom: 2px solid #000;
    padding: 3px 2px;
    height: 20px;
  }
  .color-report-table tbody tr.toplam-row td.total-col,
  .color-report-table tbody tr.toplam-row td.percentage-col,
  .color-report-table tbody tr.toplam-row td.weight-col {
    border: 1px solid #000;
    border-bottom: 2px solid #000;
    padding: 3px 2px;
    height: 20px;
  }
  .color-report-table tbody tr.factor-row td {
    border: 1px solid #000;
    border-top: 2px solid #000;
    white-space: nowrap;
    overflow: hidden;
    padding: 3px 2px;
    height: 20px;
  }
  .color-report-table tbody tr.factor-row td.total-col,
  .color-report-table tbody tr.factor-row td.percentage-col,
  .color-report-table tbody tr.factor-row td.weight-col {
    border: 1px solid #000;
    border-top: 2px solid #000;
    padding: 3px 2px;
    height: 20px;
  }
  .color-report-table tbody tr.factor-row td.factor-range-merged {
    border: 1px solid #000;
    border-top: 2px solid #000;
    border-left: 2px solid #000;
    border-right: 2px solid #000;
  }
  .factor-row .position-col {
    position: relative;
    overflow: hidden;
  }
  .factor-range-merged {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .price-cell {
    font-size: 15px;
    font-weight: 400;
    color: #000;
  }
`;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const sanitizeImageSrc = (value) => {
  const src = String(value ?? '').trim();
  if (!src) return '';

  if (src.startsWith('data:image/')) return src;
  if (src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return src;

  try {
    const parsed = new URL(src, window.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch (e) {
    return '';
  }

  return '';
};

// Returns inner markup for report
function renderTemplateMarkup(params) {
  const {
    logoUrl,
    tarihStr,
    reportData,
    fmtDecimal,
    fmtDensityDecimal,
    getCurrencySymbol,
    fmtNum,
    includePhoto = false
  } = params;

  if (!reportData) return '';

  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const cardNo = escapeHtml(reportData.CardNo || '');
  const companyName = escapeHtml(reportData.CompanyName || '');
  const fabricWidth = escapeHtml(reportData.FabricWidth || '');
  const fabricWeight = escapeHtml((reportData.FabricWeight === null || reportData.FabricWeight === undefined || reportData.FabricWeight === '' ? '' : String(reportData.FabricWeight)));
  const formatDensity = fmtDensityDecimal || fmtDecimal;
  const weftDensity = escapeHtml(formatDensity(reportData.WeftDensity) || '');
  const warpDensity = escapeHtml(formatDensity(reportData.WarpDensity) || '');
  const compositionName = escapeHtml(reportData.CompositionName || '');
  // Öncelikli olarak SampleColors'dan gelen iplik isimlerini kullan
  const warpYarn = escapeHtml(reportData.WarpYarnFromColors || reportData.WarpYarn || reportData.WarpYarnName || '');
  const weftYarn = escapeHtml(reportData.WeftYarnFromColors || reportData.WeftYarn || reportData.WeftYarnName || '');
  const reedNo = escapeHtml(reportData.ReedNo || '');
  const dgts = escapeHtml(reportData.Dgts || '');
  const reedWidth = escapeHtml(reportData.ReedWidth || '');
  const totalThread = escapeHtml(fmtNum(reportData.TotalThread) || '');
  const weaveTypeName = escapeHtml(reportData.WeaveTypeName || '');
  const grayWeft = escapeHtml(formatDensity(reportData.GrayWeft) || '');
  const grayWarp = escapeHtml(formatDensity(reportData.GrayWarp) || '');
  const price = escapeHtml(fmtDecimal(reportData.Price) || '');
  const currencySymbol = escapeHtml(getCurrencySymbol(reportData.CurrencyName, reportData.CurrencyCode));
  const note = escapeHtml(reportData.Note || '');
  const rawPhoto = String(reportData.Photo || '').trim();
  const photo = sanitizeImageSrc(rawPhoto) || (rawPhoto ? `data:image/jpeg;base64,${rawPhoto.replace(/\s/g, '')}` : '');

  // Tarak No format: ReedNo*DGTS
  const tarakNoStr = reedNo && dgts ? `${reedNo}*${dgts}` : (reedNo || dgts || '');

  // Mamül Sıklıkları: Atkı*Çözgü
  const mamulSikliklariStr = warpDensity && weftDensity ? `${warpDensity}*${weftDensity}` : (warpDensity || weftDensity || '');

  // Ham Sıklıklar: Ham Atkı*Ham Çözgü
  const hamSikliklariStr = grayWarp && grayWeft ? `${grayWarp}*${grayWeft}` : (grayWarp || grayWeft || '');

  // Price with currency
  const priceStr = price ? `${price} ${currencySymbol}` : '';

  return `
  <div class="report-wrap sample-main-page">
    <table class="info-table" style="table-layout:fixed; width:100%; border-collapse:collapse;">
      <colgroup>
        <col style="width:20%"/>
        <col style="width:55%"/>
        <col style="width:25%"/>
      </colgroup>
      <tr style="border-bottom:2px solid #000;">
        <td style="border:1px solid #000; padding:12px 8px; text-align:center;">
          <img src="${safeLogoUrl}" alt="FABCO" style="max-height:60px; max-width:100px; object-fit:contain;"/>
        </td>
        <td style="border:1px solid #000; padding:12px; text-align:center; font-weight:700; font-size:20px; letter-spacing:1px;">
          FABCO TEKSTİL KUMAŞ ANALİZ FORMU
        </td>
        <td style="border:1px solid #000; padding:8px; text-align:center;">
          <strong>Tarih:</strong> ${safeTarihStr}
        </td>
      </tr>
      <tr>
        <td class="label-cell">FİRMA ADI:</td>
        <td class="value-cell">${companyName}</td>
        <td class="value-cell"><strong>KART NO:</strong> ${cardNo}</td>
      </tr>
      <tr>
        <td class="label-cell">MAMÜL EN:</td>
        <td class="value-cell">${fabricWidth} ${fabricWidth ? 'CM' : ''}</td>
        <td class="note-cell" rowspan="12" style="vertical-align:top; padding-top:12px; word-break:break-all; overflow-wrap:break-word;"><strong>NOT:</strong> ${note}</td>
      </tr>
      <tr>
        <td class="label-cell">MAMÜL GR/M2:</td>
        <td class="value-cell">${fabricWeight} ${fabricWeight ? 'GR/M2' : ''}</td>
      </tr>
      <tr>
        <td class="label-cell">MAMÜL SIKLIKLARI:</td>
        <td class="value-cell">${mamulSikliklariStr}</td>
      </tr>
      <tr>
        <td class="label-cell">KARIŞIM:</td>
        <td class="value-cell">${compositionName}</td>
      </tr>
      <tr>
        <td class="label-cell">ÇÖZGÜ İPLİK NUMARASI:</td>
        <td class="value-cell">${warpYarn}</td>
      </tr>
      <tr>
        <td class="label-cell">ATKI İPLİK NUMARASI:</td>
        <td class="value-cell">${weftYarn}</td>
      </tr>
      <tr>
        <td class="label-cell">TARAK NO:</td>
        <td class="value-cell">${tarakNoStr}</td>
      </tr>
      <tr>
        <td class="label-cell">TARAK ENİ:</td>
        <td class="value-cell">${reedWidth} ${reedWidth ? 'CM' : ''}</td>
      </tr>
      <tr>
        <td class="label-cell">TOPLAM TEL SAYISI:</td>
        <td class="value-cell">${totalThread} ${totalThread ? 'TEL' : ''}</td>
      </tr>
      <tr>
        <td class="label-cell">ÖRGÜ:</td>
        <td class="value-cell">${weaveTypeName}</td>
      </tr>
      <tr>
        <td class="label-cell">HAM SIKLIKLARI:</td>
        <td class="value-cell">${hamSikliklariStr}</td>
      </tr>
      <tr>
        <td class="label-cell">FİYAT:</td>
        <td class="value-cell price-cell">${priceStr}</td>
      </tr>
    </table>
    ${includePhoto && photo ? `
    <div class="sample-main-photo">
      <img src="${photo}" alt="Analiz Fotoğrafı" />
    </div>
    ` : ''}
  </div>
  `;
}

// Build Excel HTML
function buildExcelHTML(params) {
  const { logoUrl, tarihStr, reportData, fmtDecimal, fmtDensityDecimal, getCurrencySymbol, fmtNum } = params;

  if (!reportData) return '';

  const safeLogoUrl = sanitizeImageSrc(logoUrl);
  const safeTarihStr = escapeHtml(tarihStr || '');
  const cardNo = escapeHtml(reportData.CardNo || '');
  const companyName = escapeHtml(reportData.CompanyName || '');
  const fabricWidth = escapeHtml(reportData.FabricWidth || '');
  const fabricWeight = escapeHtml((reportData.FabricWeight === null || reportData.FabricWeight === undefined || reportData.FabricWeight === '' ? '' : String(reportData.FabricWeight)));
  const formatDensity = fmtDensityDecimal || fmtDecimal;
  const weftDensity = escapeHtml(formatDensity(reportData.WeftDensity) || '');
  const warpDensity = escapeHtml(formatDensity(reportData.WarpDensity) || '');
  const compositionName = escapeHtml(reportData.CompositionName || '');
  // Öncelikli olarak SampleColors'dan gelen iplik isimlerini kullan
  const warpYarn = escapeHtml(reportData.WarpYarnFromColors || reportData.WarpYarn || reportData.WarpYarnName || '');
  const weftYarn = escapeHtml(reportData.WeftYarnFromColors || reportData.WeftYarn || reportData.WeftYarnName || '');
  const reedNo = escapeHtml(reportData.ReedNo || '');
  const dgts = escapeHtml(reportData.Dgts || '');
  const reedWidth = escapeHtml(reportData.ReedWidth || '');
  const totalThread = escapeHtml(fmtNum(reportData.TotalThread) || '');
  const weaveTypeName = escapeHtml(reportData.WeaveTypeName || '');
  const grayWeft = escapeHtml(formatDensity(reportData.GrayWeft) || '');
  const grayWarp = escapeHtml(formatDensity(reportData.GrayWarp) || '');
  const price = escapeHtml(fmtDecimal(reportData.Price) || '');
  const currencySymbol = escapeHtml(getCurrencySymbol(reportData.CurrencyName, reportData.CurrencyCode));
  const note = escapeHtml(reportData.Note || '');
  const photo = reportData.Photo || '';
  const photoSrc = photo && (photo.startsWith('data:image') || photo.startsWith('http'))
    ? sanitizeImageSrc(photo)
    : (photo ? `data:image/jpeg;base64,${String(photo).replace(/\s/g, '')}` : '');

  const tarakNoStr = reedNo && dgts ? `${reedNo}*${dgts}` : (reedNo || dgts || '');
  const mamulSikliklariStr = warpDensity && weftDensity ? `${warpDensity}*${weftDensity}` : (warpDensity || weftDensity || '');
  const hamSikliklariStr = grayWarp && grayWeft ? `${grayWarp}*${grayWeft}` : (grayWarp || grayWeft || '');
  const priceStr = price ? `${price} ${currencySymbol}` : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
    <title>Kumaş Analiz Formu</title>
    <style>
      .container{font-family:Arial,sans-serif;font-size:12px;}
      .main-table{border-collapse:collapse;border:2px solid #000;width:720px;}
      .note-table{border-collapse:collapse;border:2px solid #000;border-left:none;width:120px;}
      td{border:1px solid #000;padding:6px 8px;vertical-align:middle;height:22px;}
      .label{font-weight:bold;background:#ffffff;color:#000;width:25%;}
      .value{text-align:center;background:#ffffff;}
      .title{font-size:13px;font-weight:bold;text-align:center;padding:8px;}
      .header-cell{border:none;padding:6px 8px;}
      .note-header{font-weight:bold;text-align:center;border:1px solid #000;padding:4px;}
      .note-content{border:1px solid #000;vertical-align:top;padding:6px;text-align:left;height:100px;}
    </style>
  </head><body>
  <div class="container">
  <!-- Ana Tablo -->
  <table class="main-table">
    <colgroup>
      <col style="width:25%;" />
      <col style="width:25%;" />
      <col style="width:25%;" />
      <col style="width:25%;" />
    </colgroup>
    <!-- Logo, Başlık ve Tarih satırı -->
    <tr>
      <td class="header-cell" style="border-bottom:2px solid #000;vertical-align:middle;height:34px;">
        <img src="${safeLogoUrl}" alt="FABCO" style="max-height:24px;max-width:90px;"/>
      </td>
      <td class="title" colspan="2" style="border-bottom:2px solid #000;">FABCO TEKSTİL KUMAŞ ANALİZ FORMU</td>
      <td class="header-cell" style="border-bottom:2px solid #000;text-align:right;font-size:11px;">
        <strong>Tarih:</strong> ${safeTarihStr}
      </td>
    </tr>
    <!-- FİRMA ADI ve KART NO -->
    <tr>
      <td class="label">FİRMA ADI:</td>
      <td class="value" style="font-weight:bold;">${companyName}</td>
      <td class="label">KART NO:</td>
      <td class="value">${cardNo}</td>
    </tr>
    <!-- MAMÜL EN -->
    <tr>
      <td class="label">MAMÜL EN:</td>
      <td class="value">${fabricWidth}${fabricWidth ? ' CM' : ''}</td>
      <td class="value" colspan="2" rowspan="12" style="vertical-align:top; padding-top:6px;">NOT: ${note}</td>
    </tr>
    <!-- MAMÜL GR/M2 -->
    <tr>
      <td class="label">MAMÜL GR/M2:</td>
      <td class="value">${fabricWeight}${fabricWeight ? ' GR/M2' : ''}</td>
    </tr>
    <!-- MAMÜL SIKLIKLARI -->
    <tr>
      <td class="label">MAMÜL SIKLIKLARI:</td>
      <td class="value">${mamulSikliklariStr}</td>
    </tr>
    <!-- KARIŞIM -->
    <tr>
      <td class="label">KARIŞIM:</td>
      <td class="value">${compositionName}</td>
    </tr>
    <!-- ÇÖZGÜ İPLİK NUMARASI -->
    <tr>
      <td class="label">ÇÖZGÜ İPLİK NUMARASI:</td>
      <td class="value">${warpYarn}</td>
    </tr>
    <!-- ATKI İPLİK NUMARASI -->
    <tr>
      <td class="label">ATKI İPLİK NUMARASI:</td>
      <td class="value">${weftYarn}</td>
    </tr>
    <!-- TARAK NO -->
    <tr>
      <td class="label">TARAK NO:</td>
      <td class="value">${tarakNoStr}</td>
    </tr>
    <!-- TARAK ENİ -->
    <tr>
      <td class="label">TARAK ENİ:</td>
      <td class="value">${reedWidth}${reedWidth ? ' CM' : ''}</td>
    </tr>
    <!-- TOPLAM TEL SAYISI -->
    <tr>
      <td class="label">TOPLAM TEL SAYISI:</td>
      <td class="value">${totalThread}${totalThread ? ' TEL' : ''}</td>
    </tr>
    <!-- ÖRGÜ -->
    <tr>
      <td class="label">ÖRGÜ:</td>
      <td class="value">${weaveTypeName}</td>
    </tr>
    <!-- HAM SIKLIKLARI -->
    <tr>
      <td class="label">HAM SIKLIKLARI:</td>
      <td class="value">${hamSikliklariStr}</td>
    </tr>
    <!-- FİYAT -->
    <tr>
      <td class="label">FİYAT:</td>
      <td class="value" style="font-size:13px; font-weight:bold;">${priceStr}</td>
    </tr>
  </table>
  ${photoSrc ? `
  <div style="width:720px; margin-top:12px; text-align:right;">
    <img src="${photoSrc}" alt="Analiz Fotoğrafı" style="max-height:240px; max-width:100%; object-fit:contain; border:1px solid #000;"/>
  </div>
  ` : ''}
  </div>
  </body></html>`;
}
